import { Fragment, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Numbered steps shown as a progression (08/10/2026, Forge then Profil): each
 * step is a tab that always opens; done steps carry a check, the first step not
 * done is marked as current. Steps never lock each other here: the views that
 * have real prerequisites say so in their own step bar.
 */
export interface ProgressStep<K extends string> {
  key: K;
  label: string;
}

interface Props<K extends string, P extends string> {
  label: string;
  steps: ReadonlyArray<ProgressStep<K>>;
  active: K | P;
  current: K;
  done: Record<K, boolean>;
  onSelect: (key: K) => void;
  /** A secondary view at the end of the row (e.g. « Définition et sources »). */
  aside?: ReactNode;
}

export const ProgressSteps = <K extends string, P extends string = never>({ label, steps, active, current, done, onSelect, aside }: Props<K, P>) => (
  <nav aria-label={label} className="flex flex-wrap items-center gap-2">
    {steps.map(({ key, label: stepLabel }, index) => (
      <Fragment key={key}>
        {index > 0 && <span className="h-px w-6 bg-domino-cardBorder" aria-hidden="true" />}
        <button
          type="button"
          aria-current={active === key ? 'step' : undefined}
          onClick={() => onSelect(key)}
          className={cn(
            'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition',
            active === key
              ? 'border-domino-aubergine bg-domino-lilac font-semibold text-domino-aubergine'
              : 'border-domino-cardBorder text-domino-inkSoft hover:text-domino-ink',
          )}
        >
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full text-xs',
              done[key] ? 'bg-emerald-600 text-white' : key === current ? 'bg-domino-aubergine text-white' : 'border border-current',
            )}
          >
            {done[key] ? <Check className="h-3 w-3" aria-label="fait" /> : index + 1}
          </span>
          {stepLabel}
        </button>
      </Fragment>
    ))}
    {aside && <div className="ml-auto">{aside}</div>}
  </nav>
);

/** A secondary view next to the steps, styled as a quiet link. */
export const StepAside = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn('text-sm', active ? 'font-semibold text-domino-aubergine' : 'text-domino-inkSoft hover:text-domino-ink')}
  >
    {children}
  </button>
);

type Tone = 'neutral' | 'done' | 'warning' | 'error';
const TONES: Record<Tone, string> = {
  neutral: 'text-domino-ink',
  done: 'text-emerald-800',
  warning: 'text-[var(--domino-warning)]',
  error: 'text-red-700',
};

/** The head of a step: where it stands, in one sentence, and its action. */
export const StepBar = ({ text, tone = 'neutral', children, className }: { text: string; tone?: Tone; children?: ReactNode; className?: string }) => (
  <div className={cn('mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-domino-cardBorder bg-domino-white px-4 py-3', className)}>
    <p role={tone === 'error' ? 'alert' : undefined} className={cn('min-w-0 flex-1 text-sm', TONES[tone])}>{text}</p>
    {children && <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>}
  </div>
);
